/**
 * Role queries and mutations for TanStack Query (Vendor App)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPost, queryPut, queryDelete } from '../utils/request'
import type { PermissionBase } from './permissions'

// Type definitions
export type RoleType = 'primary' | 'secondary' | 'vendor'

export interface RoleListing {
  id: number
  description: string | null
  type: RoleType
  is_master: boolean
  tenant_id: number | null
}

export interface RoleDetail extends RoleListing {
  permissions: PermissionBase[]
}

export interface RoleListParams {
  limit?: number
  offset?: number
  type?: RoleType
  tenant_id?: number
  [key: string]: any
}

export interface RoleListResponse {
  items: RoleListing[]
  total: number | null
  limit: number
  offset: number
  has_more: boolean
}

export interface RoleCreate {
  description: string
  type: RoleType
  permission_ids: number[]
}

export interface RoleUpdate {
  description: string
  permission_ids: number[]
}

// Cache configuration
const CACHE_TIMES = {
  ROLES: 5 * 60 * 1000, // 5 minutes
  ROLE_DETAIL: 2 * 60 * 1000, // 2 minutes
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch a list of roles
 */
export const useRolesQuery = (params: MaybeRefOrGetter<RoleListParams> = {}) => {
  return useQuery({
    queryKey: computed(() => queryKeys.roleList(toValue(params))),
    queryFn: async (): Promise<RoleListResponse> => {
      const queryParams = toValue(params)
      return queryGet<RoleListResponse>('/v2/roles/', { params: queryParams })
    },
    staleTime: CACHE_TIMES.ROLES,
  })
}

/**
 * Query hook to fetch a single role by ID
 */
export const useRoleQuery = (roleId: MaybeRefOrGetter<number | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(roleId)
      return id ? queryKeys.role(id) : ['roles', 'null']
    }),
    queryFn: async (): Promise<RoleDetail> => {
      const id = toValue(roleId)
      if (!id) throw new Error('Role ID is required')
      return queryGet<RoleDetail>(`/v2/roles/${id}`)
    },
    staleTime: CACHE_TIMES.ROLE_DETAIL,
    enabled: computed(() => !!toValue(roleId)),
  })
}

/**
 * MUTATIONS
 */

/**
 * Mutation hook to create a new role
 */
export const useCreateRoleMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: RoleCreate): Promise<RoleDetail> => {
      return queryPost<RoleDetail>('/v2/roles/', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles() })
    },
  })
}

/**
 * Mutation hook to update an existing role
 */
export const useUpdateRoleMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ roleId, data }: { roleId: number; data: RoleUpdate }): Promise<RoleDetail> => {
      return queryPut<RoleDetail>(`/v2/roles/${roleId}`, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles() })
      queryClient.invalidateQueries({ queryKey: queryKeys.role(variables.roleId) })
    },
  })
}

/**
 * Mutation hook to delete a role
 */
export const useDeleteRoleMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (roleId: number): Promise<void> => {
      return queryDelete<void>(`/v2/roles/${roleId}`)
    },
    onSuccess: (_, roleId) => {
      queryClient.removeQueries({ queryKey: queryKeys.role(roleId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.roles() })
    },
  })
}
